// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@ces/App/Store/Reducers";
import { getSettingsCollection } from "@ces/App/Store/Selectors/SettingsSelectors";
import { getPickupTodayStatus } from "@ces/App/Store/Selectors/xPickupTodayStatusSelector";
import { USER_ROLES } from "@ces/XDL/constants";
import { FulfillmentMethod } from "@ces/XDL/types";
import { parseBoolean } from "@ces/XDL/Utilities";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasProductsWithInvalidPrice,
    hasRestrictedCartLines,
    isCartCheckoutDisabled,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export const xIsStockedItemsOnly = (state: ApplicationState): boolean =>
    getSettingsCollection(state).productSettings.displayFacetsForStockedItems;

export const xHasRole = (state: ApplicationState, role: USER_ROLES, exactMatch = false) => {
    const roles = state.context.session.userRoles;
    if (exactMatch) {
        return roles === role; // User don't have other roles
    }
    return roles?.indexOf(role) > -1;
};

export const xCanCheckoutWithCart = (state: CustomAppState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState?.value;
    return (
        cart &&
        (cart.canCheckOut ||
            hasRestrictedCartLines(cart) ||
            hasProductsWithInvalidPrice(cart) ||
            // XNG-Changes
            parseBoolean(cart?.properties?.pickupHasOutOfStock))
    );
};

export const xIsCheckoutDisabled = (state: CustomAppState): boolean => {
    const isDisabledPickupToday = getPickupTodayStatus(state);
    const isPickup = state.context.session.fulfillmentMethod === FulfillmentMethod.Pickup;
    const isPickupToday = state.context.session.properties?.pickUpType === FulfillmentMethod.PickUpToday && isPickup;
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;

    return (
        !xCanCheckoutWithCart(state) ||
        hasOnlyQuoteRequiredProducts(cart) ||
        isCartCheckoutDisabled(cart) ||
        (isDisabledPickupToday && isPickupToday) ||
        parseBoolean(cart?.properties?.pickupHasOutOfStock)
    );
};
export const getLogoImage = (state: ApplicationState) => {
    const logoImage = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields.logoImage;
    return logoImage;
};

export const getMainNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    })?.fields;
};
export const getHeaderSearchWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderSearchInput";
    })?.fields;
};

export const getCartLinkWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/CartLink";
    })?.fields;
};

export const getLogoImageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields;
};

export const getHeaderActionsWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "HeaderActions/HeaderActions";
    })?.fields;
};

export const getSecondaryNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/SecondaryNavigation";
    })?.fields;
};

export const getHeaderLinkListWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderLinkList";
    })?.fields;
};

export const getHeaderSignInWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id)
        .reverse()
        .find((widget: WidgetProps) => {
            return widget.type === "Common/HeaderSignIn";
        })?.fields;
};

export const getHeaderSpotZone = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id)
        .reverse()
        .find((widget: WidgetProps) => {
            return widget.zone === "Spot";
        })?.fields;
};

export const getJobTrackWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/JobTrack";
    })?.fields;
};

export const getAddressLocationsWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/AddressLocations";
    })?.fields;
};

export const canSubmitForCustomQuote = (cart: Cart | undefined) =>
    cart && !cart.isAwaitingApproval && !isCartEmpty(cart);
